import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { DirectionService } from '../../direction.service';
import { DialogService } from '../../dialog/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { ChatService, PriceQuotePayload } from '../../other-pages/chat/chat.service';
import { getPhCollectionServicePrice } from '../../main-section/ph-printing-table/ph-service-data';

@Component({
  selector: 'app-create-price-quote',
  templateUrl: './create-price-quote.component.html',
  styleUrls: ['./create-price-quote.component.css'],
  host: { class: 'fill-screen-modal-phone' },
})
export class CreatePriceQuoteComponent implements OnInit, AfterViewInit {
  isRTL = true;
  isDarkMode = false;
  private directionSub: Subscription;

  order: any;
  filePrices: { [key: number]: number } = {};
  fileNotes: { [key: number]: string } = {};
  totalNote = '';
  isSendingQuote = false;
  isPriceQuoteSentSuccess = false;

  /** תאריך ושעת אספקה מחייבת – מעובדים מההזמנה, ניתנים לעריכה */
  obligatedDateValue: Date | null = null;
  obligatedTimeValue = '';

  /** תאריך מינימלי לבחירה (היום) */
  get minObligatedDate(): Date {
    return new Date();
  }

  /** ערכי המקור מההזמנה (להצגת קו חוצה כשהערך שונה) */
  get sourceObligatedDate(): string {
    return this.order?.collectionData?.obligatedDate || '';
  }
  get sourceObligatedTime(): string {
    return this.order?.collectionData?.obligatedTime || '';
  }
  get sourceObligatedDay(): string {
    return this.order?.collectionData?.obligatedDay || '';
  }
  imageErrors: { [key: string]: boolean } = {};
  expressServerAdress = 'https://img-express.eazix.io';

  constructor(
    private directionService: DirectionService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private chatService: ChatService,
    @Inject(MAT_DIALOG_DATA) public data: { order: any; mode?: 'price-quote' | 'mark-production-started' | 'mark-production-finished' | 'mark-arrived-at-address' }
  ) {
    this.order = data?.order || {};
  }

  /** התחלת ייצור – מצב בדיאלוג להזנת הערה (מבנה כמו סיום ייצור) */
  get isMarkProductionStartedMode(): boolean {
    return this.data?.mode === 'mark-production-started';
  }

  /** סימון שהיצור הסתיים – מצב בדיאלוג להזנת הודעה ללקוח */
  get isMarkProductionFinishedMode(): boolean {
    return this.data?.mode === 'mark-production-finished';
  }

  /** סימון כהגעה לכתובת – מצב בדיאלוג להזנת הודעה ללקוח */
  get isMarkArrivedAtAddressMode(): boolean {
    return this.data?.mode === 'mark-arrived-at-address';
  }

  /** מצב "פשוט" – התחלת ייצור / סיום ייצור / הגעה לכתובת (כותרת + הערה + כפתורים) */
  get isSimpleMessageMode(): boolean {
    return this.isMarkProductionStartedMode || this.isMarkProductionFinishedMode || this.isMarkArrivedAtAddressMode;
  }

  productionStartedNote = '';
  productionFinishedNote = '';
  arrivedAtAddressNote = '';

  ngOnInit() {
    this.directionSub = this.directionService.direction$.subscribe((d) => (this.isRTL = d === 'rtl'));
    this.directionService.isDarkMode$.subscribe((d) => (this.isDarkMode = d));
    this.initObligatedFromOrder();
    this.syncTotalInputFromCalculated();
    if (this.hasShippingOption()) {
      this.isSyncingShipping = true;
      this.shippingInputValue = String(this.getDefaultShipping());
      this.isSyncingShipping = false;
    }
    this.updateSendButtonDisabled();
  }

  /** טוען תאריך ושעה מחייבת מההזמנה */
  private initObligatedFromOrder(): void {
    const cd = this.order?.collectionData;
    if (!cd) return;
    if (cd.obligatedDate) {
      this.obligatedDateValue = this.parseDateDdMmYyyy(cd.obligatedDate);
    }
    if (cd.obligatedTime) {
      this.obligatedTimeValue = String(cd.obligatedTime).trim();
    }
  }

  /** מפרסר מחרוזת dd/MM/yyyy ל-Date */
  private parseDateDdMmYyyy(s: string): Date | null {
    if (!s || typeof s !== 'string') return null;
    const parts = s.trim().split('/').map(Number);
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }

  /** ממיר Date לפורמט dd/MM/yyyy להשוואה */
  formatDateForCompare(d: Date | null): string {
    if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /** מחזיר את שם היום (0=ראשון .. 6=שבת) מתוך תאריך */
  getObligatedDayFromDate(d: Date | null): string {
    if (!d || !(d instanceof Date) || isNaN(d.getTime())) return '';
    const dayIndex = d.getDay();
    return this.translate.instant(`collect.day-name.${dayIndex}`);
  }

  /** האם להציג את ערך התאריך המקורי עם קו חוצה */
  get showStrikethroughDate(): boolean {
    if (!this.sourceObligatedDate) return false;
    const current = this.formatDateForCompare(this.obligatedDateValue);
    return !!current && current !== this.sourceObligatedDate;
  }

  /** האם להציג את ערך השעה המקורי עם קו חוצה */
  get showStrikethroughTime(): boolean {
    if (!this.sourceObligatedTime) return false;
    const current = (this.obligatedTimeValue || '').trim();
    return !!current && current !== this.sourceObligatedTime;
  }

  onObligatedDateChange(d: Date | null): void {
    this.obligatedDateValue = d;
    this.cdr.detectChanges();
  }

  onObligatedTimeChange(value: string): void {
    this.obligatedTimeValue = value || '';
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.updateSendButtonDisabled();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.updateSendButtonDisabled();
      this.cdr.detectChanges();
    }, 0);
  }

  ngOnDestroy() {
    this.directionSub?.unsubscribe();
  }

  closeDialog(success?: boolean) {
    this.dialogService.onCloseCreatePriceQuoteDialog(success);
  }

  onSendArrivedAtAddressMessage() {
    const orderId = this.order?._id;
    if (!orderId) {
      this.snackBar.open(this.translate.instant('chat.error-load'), '', {
        duration: 4000,
        panelClass: ['auto-width', 'center-text'].filter(Boolean),
      });
      return;
    }
    this.isSendingQuote = true;
    this.cdr.detectChanges();
    const note = (this.arrivedAtAddressNote || '').trim() || undefined;
    this.chatService.markOrderArrivedAtAddress(orderId, note).subscribe({
      next: () => {
        this.isSendingQuote = false;
        this.dialogService.onCloseCreatePriceQuoteDialog(true, false, false, true);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSendingQuote = false;
        this.cdr.detectChanges();
      },
    });
  }

  onSendProductionStartedMessage() {
    const orderId = this.order?._id;
    if (!orderId) {
      this.snackBar.open(this.translate.instant('chat.error-load'), '', {
        duration: 4000,
        panelClass: ['auto-width', 'center-text'].filter(Boolean),
      });
      return;
    }
    this.isSendingQuote = true;
    this.cdr.detectChanges();
    const note = (this.productionStartedNote || '').trim() || undefined;
    this.chatService.markProductionStarted(orderId, note).subscribe({
      next: () => {
        this.isSendingQuote = false;
        this.dialogService.onCloseCreatePriceQuoteDialog(true, true, false, false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSendingQuote = false;
        this.cdr.detectChanges();
      },
    });
  }

  onSendProductionFinishedMessage() {
    const orderId = this.order?._id;
    if (!orderId) {
      this.snackBar.open(this.translate.instant('chat.error-load'), '', {
        duration: 4000,
        panelClass: ['auto-width', 'center-text'].filter(Boolean),
      });
      return;
    }
    this.isSendingQuote = true;
    this.cdr.detectChanges();
    const note = (this.productionFinishedNote || '').trim() || undefined;
    this.chatService.markProductionFinished(orderId, note).subscribe({
      next: () => {
        this.isSendingQuote = false;
        this.dialogService.onCloseCreatePriceQuoteDialog(true, false, true, false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSendingQuote = false;
        this.cdr.detectChanges();
      },
    });
  }

  onSendPriceQuote() {
    this.updateSendButtonDisabled();
    if (this.sendButtonDisabled || this.isSendingQuote) return;
    const orderId = this.order?._id;
    if (!orderId) {
      this.snackBar.open(this.translate.instant('chat.error-load'), '', {
        duration: 4000,
        panelClass: ['auto-width', 'center-text'].filter(Boolean),
      });
      return;
    }
    const files = this.getFiles();
    const fileItems = files
      .map((file, i) => {
        const fileId = file?._id || file?.fileId;
        if (!fileId) return null;
        const price = this.filePrices[i] ?? 0;
        return {
          _id: fileId,
          price: typeof price === 'number' ? price : parseFloat(String(price)) || 0,
          note: (this.fileNotes[i] ?? '').trim() || undefined,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    const payload: PriceQuotePayload = {
      orderId,
      orderPrice: this.getEffectiveTotal(),
      orderNote: this.totalNote?.trim() || undefined,
      files: fileItems,
    };
    if (this.isPHOrder()) {
      const dateStr = this.formatDateForCompare(this.obligatedDateValue);
      const timeStr = (this.obligatedTimeValue || '').trim();
      if (dateStr) {
        payload.obligatedDate = dateStr;
        payload.obligatedDay = this.getObligatedDayFromDate(this.obligatedDateValue);
      }
      if (timeStr) payload.obligatedTime = timeStr;
      if (this.hasShippingOption()) {
        const shipping = this.getEffectiveShipping();
        payload.selectedCollectPriceModified = shipping;
      }
    }

    this.isSendingQuote = true;
    this.cdr.detectChanges();

    this.chatService.sendPriceQuote(payload).subscribe({
      next: (res) => {
        this.isSendingQuote = false;
        if (res?.success) {
          this.isPriceQuoteSentSuccess = true;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.dialogService.onCloseCreatePriceQuoteDialog(true);
          }, 1500);
        } else {
          const errKey = 'error-content-' + (res?.message || 'unknown_error');
          const errTxt = this.translate.instant(errKey);
          this.snackBar.open(errTxt !== errKey ? errTxt : this.translate.instant('error-content-unknown_error'), '', {
            duration: 5000,
            panelClass: ['auto-width', 'center-text'].filter(Boolean),
          });
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this.isSendingQuote = false;
        this.cdr.detectChanges();
        // השגיאה מוצגת על ידי ה-error-interceptor
      },
    });
  }

  getFiles(): any[] {
    return this.order?.files || [];
  }

  isPlotterOrder(): boolean {
    return this.order?.branchID && !this.order?.branchID?.is_express;
  }

  isExpressOrPH(): boolean {
    return this.order?.branchID?.is_express || this.isPHOrder();
  }

  isPHOrder(): boolean {
    return !!this.order?.collectionData;
  }

  is3DOrder(): boolean {
    return this.order?.productID?.type === '3Dprint' || this.order?.productID?.type === '3D';
  }

  getDisplayFileNameAfterSplit(file: any): string {
    if (!file) return '';
    if (file.originalFileName?.trim()) return file.originalFileName;
    if (!file.fileName) return '';
    const parts = file.fileName.split('-');
    return parts.length > 1 ? parts[1] : file.fileName;
  }

  getImgPlotter(fileIndex: number, imgIndex: number): string {
    const img = this.order?.files?.[fileIndex]?.images?.[imgIndex];
    if (!img?.thumbnailPath) return '/assets/images/box-icon.svg';
    const thumb = img.thumbnailPath;
    const server = this.order.printerID?.serverAddress || '';
    if (thumb.startsWith('uploads')) return server + '/' + thumb;
    if (thumb.startsWith('/home/ubuntu/IMG-Plotter')) return server + thumb.replace('/home/ubuntu/IMG-Plotter', '');
    return thumb;
  }

  getImgExpress(file: any): string {
    if (!this.order || !file?.images?.[0]) return '/assets/images/box-icon.svg';
    const image = file.images[0];
    if (image.path?.startsWith('/assets/') || image.path?.startsWith('assets/'))
      return image.path.startsWith('/') ? image.path : '/' + image.path;
    if (!this.order.branchID?.serial_name) return image.path || image.thumbnailPath || '/assets/images/box-icon.svg';
    if (image.thumbnailPath?.startsWith('/home/ubuntu/IMG-Express') && !this.isPHOrder())
      return image.thumbnailPath.replace('/home/ubuntu/IMG-Express', this.expressServerAdress);
    if (['PENDING', 'QR', 'NOT_PAID'].includes(this.order.status)) {
      const fn = (image.thumbnailPath || '').split(/[/\\]/).pop();
      const user = this.order.user || this.order.userID?._id || '';
      return fn ? 'https://' + this.order.branchID.serial_name + '.eazix.io/uploads/' + user + '/' + fn : image.path || image.thumbnailPath || '/assets/images/box-icon.svg';
    }
    return this.isPHOrder() ? (image.path || image.thumbnailPath) : (image.thumbnailPath || image.path) || '/assets/images/box-icon.svg';
  }

  getPaperSerialName(paperCode: string, inputBins: any[]): string | null {
    if (!paperCode || !inputBins?.length) return null;
    const p = inputBins.find((x) => x?.paperType?.paperPrinterCode === paperCode);
    return p?.paperType?.paperType || null;
  }

  getPaperSerialNameExpress(paperType: string, papers: any[]): string | null {
    if (!paperType || !papers?.length) return null;
    const p = papers.find((x) => x.paperName === paperType);
    return p?.serial_name || null;
  }

  setImageError(i: number, j: number) {
    this.imageErrors[`${i}-${j}`] = true;
  }

  hasImageError(i: number, j: number): boolean {
    return !!this.imageErrors[`${i}-${j}`];
  }

  onPriceInput(fileIndex: number, value: string) {
    const n = parseFloat(value);
    if (!isNaN(n) && n >= 0) {
      this.filePrices[fileIndex] = n;
    } else if (value === '' || value === null) {
      delete this.filePrices[fileIndex];
    }
    this.syncTotalInputFromCalculated();
    this.updateSendButtonDisabled();
    this.cdr.detectChanges();
  }

  getFilePrice(fileIndex: number): number | undefined {
    return this.filePrices[fileIndex];
  }

  getCalculatedTotal(): number {
    return this.getFiles().reduce((sum, _, i) => sum + (this.filePrices[i] ?? 0), 0);
  }

  /** עלות שירות איסוף/משלוח – ראה `ph-service-data.ts` */
  getCollectionServicePrice(service: string): number {
    return getPhCollectionServicePrice(service);
  }

  /** האם יש עלות שילוח לעריכה – נקודות איסוף או משלוח עד הבית */
  hasShippingOption(): boolean {
    if (!this.isPHOrder()) return false;
    const opt = this.order?.collectionData?.selectedCollectOption;
    return opt === 'points' || opt === 'delivery' || opt === 'distribution';
  }

  /** עלות שילוח ברירת מחדל לפי סוג האיסוף */
  getDefaultShipping(): number {
    const opt = this.order?.collectionData?.selectedCollectOption;
    return this.getCollectionServicePrice(opt || '');
  }

  /** עלות שילוח אפקטיבית – ערך שערך המשתמש או ברירת מחדל */
  getEffectiveShipping(): number {
    if (this.shippingOverride != null && !isNaN(this.shippingOverride) && this.shippingOverride >= 0) {
      return this.shippingOverride;
    }
    return this.getDefaultShipping();
  }

  /** סה"כ מחושב כולל עלות משלוח/איסוף (כשמתאים) */
  getCalculatedTotalWithCollection(): number {
    const printing = this.getCalculatedTotal();
    if (!this.isPHOrder()) return printing;
    return printing + this.getDefaultShipping();
  }

  /** עיגול כלפי מטה ל־2 ספרות אחרי הנקודה */
  private roundDownTo2Decimals(n: number): number {
    if (n == null || isNaN(n)) return 0;
    return Math.floor(n * 100) / 100;
  }

  /** סה"כ לשליחה – הדפסה + משלוח (בהזמנות PH). לתצוגה באינפוט: רק הדפסה. הסה"כ מעוגל כלפי מטה ל־0.00 */
  getEffectiveTotal(): number {
    const printing = this.totalOverride ?? this.getCalculatedTotal();
    if (!this.isPHOrder()) return this.roundDownTo2Decimals(printing);
    return this.roundDownTo2Decimals(printing + this.getEffectiveShipping());
  }

  sendButtonDisabled = true;

  /** הכפתור disabled כשהאינפוט של הסה"כ (הדפסה בלבד, בלי משלוח) אינו גדול מ-0 */
  private updateSendButtonDisabled(): void {
    const str = (this.totalInputValue ?? '').toString().trim();
    const parsed = str === '' ? null : parseFloat(str);
    const totalInput = (parsed !== null && !isNaN(parsed)) ? parsed : (this.totalOverride ?? this.getCalculatedTotal());
    const isValid = typeof totalInput === 'number' && !isNaN(totalInput) && totalInput > 0;
    this.sendButtonDisabled = !isValid;
  }

  get showStrikethroughTotal(): boolean {
    return this.totalOverride != null && this.totalOverride !== this.getCalculatedTotal();
  }

  /** עלות ייצור לתצוגה – ערך השדה בעת הקלדה או אחרי blur */
  getDisplayPrinting(): number {
    const str = (this.totalInputValue ?? '').toString().trim();
    if (str !== '') {
      const n = parseFloat(str);
      if (!isNaN(n) && n >= 0) return n;
    }
    return this.totalOverride ?? this.getCalculatedTotal();
  }

  /** סה"כ לתצוגה בשורת הפירוט – מתעדכן בזמן הקלדה. מעוגל כלפי מטה ל־0.00 */
  getDisplayTotal(): number {
    if (!this.isPHOrder()) return this.roundDownTo2Decimals(this.getDisplayPrinting());
    return this.roundDownTo2Decimals(this.getDisplayPrinting() + this.getEffectiveShipping());
  }

  /** פירוט להצגה – כשיש עלות משלוח/איסוף; מתעדכן בזמן הקלדה בעלות ייצור */
  get totalBreakdown(): { printing: number; shipping: number } | null {
    if (!this.isPHOrder()) return null;
    const shipping = this.getEffectiveShipping();
    if (shipping <= 0) return null;
    const printing = this.getDisplayPrinting();
    return printing >= 0 ? { printing, shipping } : null;
  }

  totalOverride: number | null = null;
  totalInputValue = '';
  /** ערך עריכה לעלות שילוח – רק כשיש נקודות איסוף או משלוח עד הבית */
  shippingOverride: number | null = null;
  shippingInputValue = '';
  private isSyncingFromCalculated = false;
  private isSyncingShipping = false;

  onTotalInputChange(): void {
    this.updateSendButtonDisabled();
  }

  onShippingInputChange(): void {
    const str = (this.shippingInputValue ?? '').toString().trim();
    if (str === '') {
      this.shippingOverride = null;
    } else {
      const n = parseFloat(str);
      this.shippingOverride = !isNaN(n) && n >= 0 ? n : null;
    }
    this.cdr.detectChanges();
  }

  onShippingBlur(): void {
    if (this.isSyncingShipping) return;
    const str = (this.shippingInputValue ?? '').toString().trim();
    if (str === '') {
      this.shippingOverride = null;
      this.shippingInputValue = this.hasShippingOption() ? String(this.getDefaultShipping()) : '';
    } else {
      const n = parseFloat(str);
      if (!isNaN(n) && n >= 0) {
        this.shippingOverride = n;
        this.shippingInputValue = String(n);
      } else {
        this.shippingOverride = null;
        this.shippingInputValue = this.hasShippingOption() ? String(this.getDefaultShipping()) : '';
      }
    }
    this.cdr.detectChanges();
  }

  onTotalBlur() {
    if (this.isSyncingFromCalculated) return;
    const str = (this.totalInputValue ?? '').toString().trim();
    if (str === '') {
      this.totalOverride = null;
      this.totalInputValue = '';
    } else {
      const n = parseFloat(str);
      if (!isNaN(n) && n >= 0) {
        this.totalOverride = n;
        this.totalInputValue = String(n);
      } else {
        this.totalOverride = null;
        this.syncTotalInputFromCalculated();
      }
    }
    this.updateSendButtonDisabled();
    this.cdr.detectChanges();
  }

  /** סנכרון האינפוט מהסכום המחושב – רק הדפסה (ללא משלוח), מתחיל ב־0 */
  private syncTotalInputFromCalculated(): void {
    if (this.totalOverride != null) return;
    this.isSyncingFromCalculated = true;
    const calc = this.getCalculatedTotal();
    this.totalInputValue = calc === 0 ? '' : String(calc);
    this.isSyncingFromCalculated = false;
  }

  getOrderFilesSystem(order: any): string {
    if (order?.branchID?.is_express || this.isPHOrder()) return 'express';
    return 'plotter';
  }

  onDownloadOriginalFile(
    service: string,
    imgIndex: number,
    file: any,
    isOriginal: boolean | 'processedNotSent' = true
  ) {
    if (isOriginal === false && this.order?.status === 'PENDING') return;
    let path = '';
    if (service === 'plotter') {
      const serverAddress = this.order.printerID?.serverAddress || '';
      const img = isOriginal === true
        ? (file.filePath && file.fileName) ? file.filePath + '/' + file.fileName : ''
        : file.images?.[imgIndex]?.resizedPath || '';
      const modifiedImg = img.replace('/home/ubuntu/IMG-Plotter/', '');
      path = serverAddress + '/' + modifiedImg;
    } else if (service === 'express') {
      if (isOriginal === true) {
        const username = this.order?.user || this.order?.userID?._id || '';
        path = this.expressServerAdress + '/uploads/' + username + '/originals/' + file.fileName;
      } else if (isOriginal === 'processedNotSent') {
        const modifiedImg = (file.filePath || '').replace('/home/ubuntu/IMG-Express/', '');
        path = this.expressServerAdress + '/' + modifiedImg + '/' + file.fileName;
      } else {
        path = file.pdfPath || '';
      }
    }
    if (path) {
      const fileName = path.split('/').pop();
      fetch(path)
        .then((res) => res.ok ? res.blob() : Promise.reject(new Error('File not found')))
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          const link = document.createElement('a');
          link.href = path;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    }
  }

  private get3DFileDownloadUrl(file: any): string | null {
    if (!file) return null;
    const url = file.fileUrl || file.FileUrl || file.url ||
      file.settings?.fileUrl || file.settings?.FileUrl ||
      file.printSettings?.fileUrl || file.printSettings?.FileUrl;
    if (typeof url !== 'string' || !url.trim()) return null;
    if (url.includes('box-icon') || url.includes('assets/') || url.endsWith('.svg')) return null;
    return url;
  }

  private download3DFileByFileId(fileId: string, fileName: string) {
    const base = (environment.apiUrl || '').replace(/\/+$/, '');
    const url = `${base}/files/download3dprintfile/${fileId}`;
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = (fileName || 'file.stl').trim();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      },
      error: () => {
        this.snackBar.open(this.translate.instant('su-management.users.download-error'), '', {
          duration: 4000,
          panelClass: ['auto-width', 'center-text'].filter(Boolean),
        });
      },
    });
  }

  onDownload3DFile(file: any) {
    const url = this.get3DFileDownloadUrl(file);
    const fileId = file?.fileId || file?._id;
    const fileName = (file?.fileName || file?.name || file?.originalFileName || 'file.stl').trim();
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    if (fileId) {
      this.download3DFileByFileId(fileId, fileName);
    }
  }
}
